import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { EditSheet, DangerAction, changedFields, toField } from '../ui/EditSheet';
import { Field, field } from '../create/Field';

/**
 * Fix a customer's details.
 *
 * The plain-looking half of the product that was missing entirely: a name typed wrong at
 * booking, a customer who moved, a number that reaches the wrong person. All of it was
 * permanent, and the only workaround — a second customer record — was permanent too.
 */
export function EditCustomerSheet({
  open,
  onClose,
  customer,
}: {
  open: boolean;
  onClose: () => void;
  customer: {
    id: string;
    kind: string;
    displayName: string;
    companyName: string | null;
    email: string | null;
    phone: string | null;
    billingCity: string | null;
    billingState: string | null;
    leadSource: string | null;
    taxExempt: boolean;
  };
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const original = {
    kind: customer.kind as 'residential' | 'commercial',
    displayName: customer.displayName,
    companyName: toField(customer.companyName),
    email: toField(customer.email),
    phone: toField(customer.phone),
    city: toField(customer.billingCity),
    state: toField(customer.billingState),
    leadSource: toField(customer.leadSource),
  };
  const [draft, setDraft] = useState(original);
  const [exempt, setExempt] = useState(customer.taxExempt);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const changed = changedFields(original, draft);
  const dirty = Object.keys(changed).length > 0 || exempt !== customer.taxExempt;

  const save = useMutation(
    trpc.customer.update.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );

  const archive = useMutation(
    trpc.customer.archive.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        // Their page no longer resolves, so staying on it would show a "gone" screen.
        void navigate({ to: '/customers' });
      },
    }),
  );

  return (
    <EditSheet
      open={open}
      onClose={onClose}
      title={`Edit ${customer.displayName}`}
      dirty={dirty}
      saving={save.isPending}
      error={save.error ?? archive.error}
      onSave={() =>
        save.mutate({
          customerId: customer.id,
          ...changed,
          ...(exempt !== customer.taxExempt ? { taxExempt: exempt } : {}),
          ...idem(),
        })
      }
      destructive={
        <DangerAction
          label="Archive this customer"
          question={`Archive ${customer.displayName}?`}
          detail="They come off your lists and out of the pickers. Their invoices, jobs and history all stay exactly as they are, and you can bring them back."
          pending={archive.isPending}
          onConfirm={() => archive.mutate({ id: customer.id, ...idem() })}
        />
      }
    >
      <Field label="Name">
        <input
          className={field}
          value={draft.displayName}
          onChange={(e) => set('displayName')(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label="Type">
        <select
          className={field}
          value={draft.kind}
          onChange={(e) => set('kind')(e.target.value)}
        >
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
        </select>
      </Field>
      <Field label="Company">
        <input
          className={field}
          value={draft.companyName}
          onChange={(e) => set('companyName')(e.target.value)}
          placeholder="Only if they bill under a business name"
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Email">
          <input
            className={field}
            value={draft.email}
            onChange={(e) => set('email')(e.target.value)}
            inputMode="email"
          />
        </Field>
        <Field label="Phone">
          <input
            className={field}
            value={draft.phone}
            onChange={(e) => set('phone')(e.target.value)}
            inputMode="tel"
          />
        </Field>
        <Field label="City">
          <input
            className={field}
            value={draft.city}
            onChange={(e) => set('city')(e.target.value)}
          />
        </Field>
        <Field label="State">
          <input
            className={field}
            value={draft.state}
            onChange={(e) => set('state')(e.target.value.toUpperCase())}
            maxLength={2}
          />
        </Field>
      </div>
      <Field label="How they found you">
        <input
          className={field}
          value={draft.leadSource}
          onChange={(e) => set('leadSource')(e.target.value)}
          placeholder="Referral, Google, truck…"
        />
      </Field>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={exempt}
          onChange={(e) => setExempt(e.target.checked)}
        />
        <span>
          Tax exempt
          <span className="block text-xs text-muted-foreground">
            Nothing this customer is billed gets sales tax. Keep their certificate on file
            — this is the flag your invoices are judged on.
          </span>
        </span>
      </label>

      {/* Clearing a box is a real edit, and people don't expect that from a form. */}
      <p className="text-xs text-muted-foreground">
        Emptying a box clears it. Anything you don't touch stays as it is.
      </p>
    </EditSheet>
  );
}
